import {
  Div,
  Flex,
  Input,
  LoadingSpinner,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import React, { useCallback, useContext, useEffect } from 'react'
import useDebounce from 'src/hooks/useDebounce'
import { ValidateUsername, ValidateUsernameData } from 'src/graphql'
import { performOperation } from 'src/app/api'
import { PromoContext } from '../context/promoContext'

type CreateAccountProps = {
  isMobile: boolean
  isCreatingAccount: boolean
}

export interface CreateAccountFormState {
  email: string
  username: string
  password: string
}

const CreateAccountForm: React.FC<CreateAccountProps> = props => {
  const { isMobile, isCreatingAccount } = props

  const {
    state: { create },
    dispatch,
  } = useContext(PromoContext)

  const { isCheckingUsername, form, isUsernameTaken } = create

  const handleChange = (name: keyof CreateAccountFormState, value: string) => {
    const sanitizedValue =
      name === 'username' ? value.replace(/[^a-z0-9_\-@.+]|\s+/gim, '') : value
    dispatch({
      type: 'SET_CREATE_FORM',
      payload: { ...create.form, [name]: sanitizedValue },
    })
  }

  const debouncedUsername = useDebounce(form.username, 500)

  const setIsCheckingUsername = React.useCallback(
    (isChecking: boolean) => {
      dispatch({
        type: 'SET_IS_CHECKING_USERNAME',
        payload: { isCheckingUsername: isChecking },
      })
    },
    [dispatch],
  )

  const setIsUsernameTaken = useCallback(
    (isTaken: boolean) => {
      dispatch({
        type: 'SET_IS_USERNAME_TAKEN',
        payload: {
          isUsernameTaken: isTaken,
        },
      })
    },
    [dispatch],
  )

  useEffect(() => {
    if (debouncedUsername) {
      setIsCheckingUsername(true)
      performOperation<ValidateUsernameData>(
        ValidateUsername({ username: debouncedUsername }),
        result => {
          setIsCheckingUsername(false)
          if (result.value) {
            setIsUsernameTaken(!result.value.validateUsername.valid)
          }
        },
      )
    }
  }, [debouncedUsername, setIsCheckingUsername, setIsUsernameTaken])

  return (
    <>
      {isCreatingAccount && (
        <Div overflow="hidden" outset={{ top: 'x6', bottom: 'x3' }}>
          <LoadingSpinner size="xLarge" />
        </Div>
      )}
      {!isCreatingAccount && (
        <Div overflow="hidden">
          <Spacer space="x1" />
          <Text
            type="body"
            weight="bold"
            content="Step 1: Create Your Account"
            color="primaryHeading"
            style={{ textDecoration: 'underline' }}
          />
          <Spacer space={'x1'} />
          <Flex width="100%" flexDirection="column">
            <Input
              onChange={email => handleChange('email', email)}
              type="email"
              name="email"
              value={form.email}
              placeholder="Email Address"
              focusColor="primaryBrand"
              isFullWidth={true}
            />
            <Spacer space={'x1_5'} />
            <Input
              onChange={username => handleChange('username', username)}
              type="text"
              name="username"
              value={form.username}
              placeholder="@username"
              focusColor="primaryBrand"
              message={
                isUsernameTaken
                  ? {
                      type: 'danger',
                      content: 'This username is taken',
                      icon: 'information',
                      onClick: () => {},
                    }
                  : undefined
              }
              isFullWidth={true}
              isLoading={isCheckingUsername}
            />
            <Text
              type="footnote"
              content={'Usernames are case sensitive'}
              outset={{ horizontal: 'x1' }}
            />
            <Spacer space={'x1_5'} />
            <Input
              onChange={password => handleChange('password', password)}
              type="password"
              name="password"
              value={form.password}
              placeholder="Password"
              focusColor="primaryBrand"
              isFullWidth={true}
            />
            <Spacer space={isMobile ? 'x1' : 'x4'} />
          </Flex>
        </Div>
      )}
    </>
  )
}

export default CreateAccountForm
